import { useFormatToDecimal, useTranslation } from '@dreamstack/i18n'
import { SimpleDefinitionList } from '@dreamstack/simple-components/src/lib/SimpleDefinitionList'
import { cn } from '@dreamstack/util'
import type { FullProperty } from '@dreamstack/web/lib/prisma/properties'
import type { EquipmentFeature } from './EquipmentFeatures'
import filter from 'lodash/filter'
import type { FunctionComponent } from 'react'
import {
  Css_Text_16_24_tw,
  PropertyRoomAndSqmText,
  PropertySmallText,
} from '../layout/Texts'
import { EquipmentFeatures } from './EquipmentFeatures'

export const PropertyInformationList: FunctionComponent<
  React.PropsWithChildren<{
    property?: FullProperty
  }>
> = ({ property }) => {
  const t = useTranslation()
  const formatDecimal = useFormatToDecimal()
  if (!property) return null
  const count = property.number_of_rooms ?? 0
  const sqm = property.property_space_value
    ? formatDecimal(property.property_space_value) + ' '
    : '-'
  const floor =
    property.floor === '0'
      ? t('accentro:property.groundFloorAbbreviation')
      : property.floor === 'null' // check for null has to be with null as a string, since propstack returns a string 'null' when the floor is not set in the property
      ? '-'
      : property.floor + '.'
  const constructionYear = property.construction_year?.toString() || '-'
  const externalId = property?.name || '-'

  // EquipmentFeatures with value = true
  let equipmentFeatures: EquipmentFeature[] = property.equipment_features || []
  equipmentFeatures = filter(equipmentFeatures, (f) => !!f.value && !!f.label)

  return (
    <div
      className={cn(
        'grid grid-cols-5 py-px-24 mt-px-32 overflow-x-hidden border-t-2 border-b-2 border-accentroGray-200',
        'md:py-px-48 md:mt-px-48'
      )}
    >
      <div
        className={cn(
          'col-span-5 grid grid-cols-2 md:grid-cols-3 gap-y-px-24',
          'ml:grid-cols-5 ml:gap-y-px-32'
        )}
      >
        <SimpleDefinitionList
          big
          dt={<PropertyRoomAndSqmText>{count}</PropertyRoomAndSqmText>}
          dd={
            <PropertySmallText>
              {t('accentro:room.rooms', { count })}
            </PropertySmallText>
          }
        />
        <SimpleDefinitionList
          big
          dt={
            <PropertyRoomAndSqmText>
              {`${sqm}`}
              {sqm !== '-' && (
                <span className={cn('font-semibold', Css_Text_16_24_tw)}>
                  {t('accentro:squareMeter')}
                </span>
              )}
            </PropertyRoomAndSqmText>
          }
          dd={
            <PropertySmallText>{t('accentro:livingSpace')}</PropertySmallText>
          }
        />
        <SimpleDefinitionList
          big
          dt={<PropertyRoomAndSqmText>{floor}</PropertyRoomAndSqmText>}
          dd={<PropertySmallText>{t('accentro:floor')}</PropertySmallText>}
        />
        <SimpleDefinitionList
          big
          dt={
            <PropertyRoomAndSqmText>{constructionYear}</PropertyRoomAndSqmText>
          }
          dd={
            <PropertySmallText>
              {t('accentro:yearOfConstruction')}
            </PropertySmallText>
          }
        />
        <SimpleDefinitionList
          big
          dt={<PropertyRoomAndSqmText>{externalId}</PropertyRoomAndSqmText>}
          dd={
            <PropertySmallText>
              {t('accentro:propertyDetails.property-external-id')}
            </PropertySmallText>
          }
        />
      </div>

      {equipmentFeatures?.length >= 1 && (
        <EquipmentFeatures equipment={equipmentFeatures} />
      )}
    </div>
  )
}
